/**
 * Thin fetch wrapper for the Worker API.
 *
 * Host calls carry the Clerk session token as `Authorization: Bearer …`. It has
 * to be a header rather than a cookie: Clerk's `__session` cookie is scoped to
 * the Clerk Frontend API domain, so it is not attached to requests to this
 * Worker — and cannot be, in development where the SPA is on :5173 and the
 * Worker on :8787.
 *
 * Guest calls (upload, like, comment) send no header at all. That is correct:
 * those routes accept a guest code or an uploader key instead, and the server
 * resolves the host only when one is present.
 */

import type { ApiErrorBody, ApiErrorCode } from "@shared";

import { readSessionToken } from "./session-token.js";

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    const token = await readSessionToken();
    response = await fetch(path, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError("upstream_error", "Could not reach the server.", 0);
  }

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const parsed = body as ApiErrorBody | null;
    throw new ApiError(
      parsed?.error.code ?? "internal",
      parsed?.error.message ?? `Request failed with ${response.status}.`,
      response.status,
      parsed?.error.details,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** PUT carries the full replacement (album media sets), so retries are safe. */
  put: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /**
   * DELETE with an optional body.
   *
   * `fetch` refuses a body on GET and HEAD only, so this is legal — and it is
   * the only way to tell the server *which* guest is unliking or deleting their
   * own comment, since the session cookie is not present on a guest's device.
   */
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),
};

export interface HealthResponse {
  readonly ok: boolean;
  readonly bindings: Record<string, boolean>;
}